/**
 * VFXScriptApi — the `engine.vfx` capability module (global service), entity-addressed.
 *
 * Lets scripts drive a VFXEmitter's particle system (play/stop/spawn/loop) by entity. The
 * concrete backing is injected per runtime (DI): the player/editor map an entity → its live
 * emitter via the VFX subsystem (GUID-keyed), so this module stays dependency-free in core.
 * Mirrors AudioScriptApi/PhysicsScriptApi.
 */

import { scriptApi, scriptFn } from "../scriptApi";
import type { EntityHandle } from "../IEngineContext";

/** The injected backing the vfx module resolves against (DI per runtime). */
export interface VFXBacking {
  play(entity: EntityHandle): void;
  stop(entity: EntityHandle): void;
  spawn(entity: EntityHandle, count: number): void;
  setLoop(entity: EntityHandle, loop: boolean): void;
  isComplete(entity: EntityHandle): boolean;
  setVariable(entity: EntityHandle, name: string, value: unknown): boolean;
}

@scriptApi("vfx", { tier: "safe", version: 1 })
export class VFXScriptApi {
  constructor(private readonly _backing: VFXBacking) {}

  @scriptFn() play(entity: EntityHandle): void {
    this._backing.play(entity);
  }

  @scriptFn() stop(entity: EntityHandle): void {
    this._backing.stop(entity);
  }

  @scriptFn() spawn(entity: EntityHandle, count: number): void {
    this._backing.spawn(entity, count);
  }

  @scriptFn() setLoop(entity: EntityHandle, loop: boolean): void {
    this._backing.setLoop(entity, loop);
  }

  @scriptFn() isComplete(entity: EntityHandle): boolean {
    return this._backing.isComplete(entity);
  }

  /**
   * Set an exposed graph variable by name (float/int → number, vec2 → {x,y},
   * vec3 → {x,y,z}, color → {r,g,b,a}). Returns false when the entity has no
   * live emitter or the variable name is unknown.
   */
  @scriptFn() setVariable(entity: EntityHandle, name: string, value: unknown): boolean {
    return this._backing.setVariable(entity, name, value);
  }
}
